import React, { useState } from 'react';

import { Eye, EyeOff } from '@/components/SVG';
import Touchable from '@/components/Touchable';
import Typography from '@/components/Typography';
import { useTheme } from '@emotion/react';

import { TextFieldProps } from './TextField.props';
import {
  Container,
  LeftComponentContainer,
  FieldContainer,
  Field,
  VisibilityContainer,
  Error,
  LabelMain,
  RightComponentContainer
} from './TextField.style';

const TextField = (props: TextFieldProps): JSX.Element => {
  const theme = useTheme();
  const {
    id,
    type,
    label,
    value = '',
    disabled = false,
    onChange = () => null,
    onChangeText = () => null,
    placeholder,
    secured,
    error = '',
    LeftComponent = null,
    RightComponent = null,
    noLeftCompBorder = false,
    noRightCompBorder = false,
    className = undefined,
    onBlur = () => null,
    onFocus = () => null,
    onPaste,
    onKeyDown = () => null,
    autoFocus = false,
    labelStyle = undefined,
    height,
    LeftComponentWidth,
    bills,
    autoComplete = 'on',
    staticText,
    RightComponentWidth,
    onClickIcon = () => null,
    isColorBlack,
    style,
    prefix,
    highlightError,
    containerStyle
  } = props;

  const [showSecuredText, setShowSecuredText] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
    onChangeText(event.target.value);
  };

  const VisibilityIcon = showSecuredText ? EyeOff : Eye;
  const defaultInputType = type || 'text';
  return (
    <Container className={className} style={containerStyle}>
      <LabelMain
        variant={labelStyle === undefined ? 'overline' : labelStyle}
        type={labelStyle === undefined ? 'label' : labelStyle}
        color={isColorBlack ? 'black' : 'shade6'}
      >
        {label}
      </LabelMain>
      <FieldContainer bills={bills} error={error} highlightError={highlightError}>
        {LeftComponent && (
          <LeftComponentContainer
            noLeftCompBorder={noLeftCompBorder}
            LeftComponentWidth={LeftComponentWidth}
            onClick={onClickIcon}
            clickable={!!onClickIcon}
            theme={theme}
          >
            {LeftComponent}
          </LeftComponentContainer>
        )}
        {staticText && (
          <Typography variant="label" color="shade6">
            {staticText}
          </Typography>
        )}
        <Field
          staticText={staticText}
          bills={bills}
          style={{ fontSize: '14px', lineHeight: '24px', ...style, height: height }}
          id={id}
          autoFocus={autoFocus}
          disabled={disabled}
          type={secured && !showSecuredText ? 'password' : defaultInputType}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          prefix={prefix}
        />
        {secured && (
          <VisibilityContainer>
            <Touchable dark onClick={() => setShowSecuredText((v) => !v)}>
              <VisibilityIcon fill={theme.brand.primary} />
            </Touchable>
          </VisibilityContainer>
        )}
        {RightComponent && (
          <RightComponentContainer
            theme={theme}
            onClick={onClickIcon}
            clickable={!!onClickIcon}
            RightComponentWidth={RightComponentWidth}
            noRightCompBorder={noRightCompBorder}
          >
            {RightComponent}
          </RightComponentContainer>
        )}
      </FieldContainer>
      {(error || '').length > 0 && (
        <Error variant="caption" color="error" className="error">
          {error}
        </Error>
      )}
    </Container>
  );
};

export default TextField;
